import { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthenticatedApi } from '../api';

/**
 * Simple hook to check if user has active subscription
 * Returns: { hasSubscription: boolean, subscriptionPlan: string | null, isLoading: boolean, error: Error | null }
 */
export function useSubscriptionCheck(justProcessedCheckoutRef) {
  const { isAuthenticated, user } = useAuth0();
  const { authenticatedFetch } = useAuthenticatedApi();
  const authenticatedFetchRef = useRef(authenticatedFetch);
  authenticatedFetchRef.current = authenticatedFetch;
  const lastKnownHasSubscriptionRef = useRef(null);

  const [hasSubscription, setHasSubscription] = useState(null); // null = not checked yet, true/false = checked
  const [subscriptionPlan, setSubscriptionPlan] = useState(null); // 'basic', 'professional', 'premium', or null
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      setHasSubscription(null);
      return;
    }
    // Logged in but no email on profile — cannot verify; don't leave hasSubscription stuck at null
    if (!user?.email) {
      setIsLoading(false);
      setHasSubscription(false);
      setSubscriptionPlan(null);
      return;
    }

    // Skip check if we just processed checkout (prevents double loading)
    // This gives the database time to update, especially important in production
    if (justProcessedCheckoutRef?.current) {
      return;
    }

    let cancelled = false;

    const baseUrl = `${process.env.REACT_APP_API_URL || '/api'}/members/by-email?email=${encodeURIComponent(user.email)}`;
    
    const checkSubscription = async (retryCount = 0, url = baseUrl) => {
      try {
        setIsLoading(true);
        setError(null);
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Subscription check timeout')), 5000)
        );
        
        const fetchPromise = authenticatedFetchRef.current(url);
        const response = await Promise.race([fetchPromise, timeoutPromise]);

        if (cancelled) return;

        // 304 = cached response with empty body - response.json() would throw. Retry with cache-bust to get full body
        if (response.status === 304 && retryCount < 2) {
          const separator = baseUrl.includes('?') ? '&' : '?';
          const cacheBustUrl = `${baseUrl}${separator}_=${Date.now()}`;
          return checkSubscription(retryCount + 1, cacheBustUrl);
        }

        // 401 = token not ready or Auth0 auth error - retry with backoff (Auth0 may be slow to respond)
        if (response.status === 401) {
          // Expired/stale session: authenticatedFetch returns a synthetic 401 (code AUTH_REQUIRED)
          // and is already redirecting to Auth0 to re-authenticate. Do NOT flip to
          // hasSubscription=false here — that would bounce a still-valid subscriber to /plans
          // before the re-login completes. Leave the prior state and let navigation take over.
          let authRequired = false;
          try {
            const body = await response.clone().json();
            authRequired = body?.code === 'AUTH_REQUIRED';
          } catch {
            // non-JSON / empty body -> treat as a normal transient 401 below
          }
          if (authRequired) {
            return;
          }
          const maxRetries = 4;
          if (retryCount < maxRetries) {
            const delayMs = 600 * Math.pow(2, retryCount);
            await new Promise(r => setTimeout(r, delayMs));
            if (cancelled) return;
            return checkSubscription(retryCount + 1);
          }
          setError(new Error('Unable to verify subscription. Authentication may be delayed. Please try again or refresh the page.'));
          setHasSubscription(false);
          setSubscriptionPlan(null);
          return;
        }
        
        if (response.ok) {
          let data;
          try {
            data = await response.json();
          } catch (parseError) {
            throw new Error('Invalid response format from server');
          }
          
          // Check subscription status - only look at status field for authentication
          // The subscription field (basic/professional/premium) is only for access control, not authentication
          // Treat 'canceling' the same as 'subscribed' - user still has access until period ends
          const statusValue = data?.status ? String(data.status).trim().toLowerCase() : null;
          const isSubscribed = statusValue === 'subscribed' || statusValue === 'canceling';
          lastKnownHasSubscriptionRef.current = isSubscribed;
          setHasSubscription(isSubscribed);
          // Store subscription plan name (basic, professional, premium) for access control
          setSubscriptionPlan(isSubscribed && data?.subscription ? data.subscription.toString().trim().toLowerCase() : null);
        } else {
          lastKnownHasSubscriptionRef.current = false;
          setError(new Error(`Failed to check subscription: ${response.status} ${response.statusText}`));
          setHasSubscription(false);
          setSubscriptionPlan(null);
        }
      } catch (err) {
        if (cancelled) return;
        // Don't flip to false on transient errors if we previously had a valid subscription
        if (lastKnownHasSubscriptionRef.current === true) {
          setHasSubscription(true);
          setError(null);
        } else {
          setError(err);
          setHasSubscription(false);
          setSubscriptionPlan(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    checkSubscription();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Omit authenticatedFetch; it changes on
  // every Auth0 context update (e.g. when other API calls fetch tokens), causing spurious re-runs.
  }, [isAuthenticated, user?.email, refetchTrigger]);

  const refetch = () => {
    setRefetchTrigger(prev => prev + 1);
  };

  const setSubscriptionStatus = (status) => {
    // Treat 'canceling' the same as 'subscribed' - user still has access until period ends
    const isSubscribed = status === 'subscribed' || status === 'canceling';
    setHasSubscription(isSubscribed);
    // Note: setSubscriptionStatus doesn't set plan name - it's set from the API response
    setIsLoading(false);
  };

  return { hasSubscription, subscriptionPlan, isLoading, error, refetch, setSubscriptionStatus };
}
